import DOMPurify from 'dompurify';

/**
 * Single source of truth for sanitizing externally-sourced (CMS / API) rich
 * text before it is bound into the DOM via `[innerHTML]`.
 *
 * Rationale: several views render CMS-authored HTML (blog descriptions, FAQ
 * answers, project descriptions/render-services). Previously these were passed
 * through `DomSanitizer.bypassSecurityTrustHtml()`, which disables Angular's
 * built-in sanitizer and allows stored-XSS payloads (e.g.
 * `<img src=x onerror=...>`) to execute in our origin. We now clean the markup
 * with DOMPurify at the point of receipt and bind the plain string, so
 * Angular's own sanitizer still runs as a second layer.
 *
 * Keep sanitizer configuration here only — do not scatter `DOMPurify.sanitize`
 * calls with inline options across the app.
 *
 * NOTE: DOMPurify requires a DOM, so callers must invoke this in the browser
 * (guard with `isPlatformBrowser`); during SSR the CMS HTML is bound as-is and
 * re-hydrated/sanitized on the client.
 */
export function sanitizeCmsHtml(dirty: string | null | undefined): string {
  return DOMPurify.sanitize(dirty ?? '', baseConfig());
}

/**
 * As `sanitizeCmsHtml`, but keeps `target` on links.
 *
 * For AI assistant replies (see `utils/markdown.ts`), whose links point at quote and order
 * pages. Those have to open in a new tab: following one in the same tab unmounts the chat
 * panel and loses the conversation, since the thread is held in memory only.
 *
 * `target` is not in DOMPurify's allow-list at all — not forbidden, simply absent — so it is
 * silently dropped without `ADD_ATTR`, which is why the generated `target="_blank"` never
 * reached the DOM. (`rel` *is* allowed by default, so `noopener noreferrer` survives on its
 * own.) Angular's own sanitizer permits `target`, so nothing downstream removes it again.
 *
 * Kept separate from `sanitizeCmsHtml` deliberately: CMS-authored HTML has no reason to open
 * new tabs, and widening the shared profile would change blog, FAQ and project markup too.
 */
export function sanitizeAiMarkdownHtml(dirty: string | null | undefined): string {
  return DOMPurify.sanitize(dirty ?? '', { ...baseConfig(), ADD_ATTR: ['target'] });
}

/**
 * Shared hardening applied to every externally-sourced fragment. Returned fresh per call
 * rather than held as a shared constant: DOMPurify's config type wants mutable arrays, and a
 * single shared object could be mutated by one caller for all the others.
 */
function baseConfig() {
  return {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['on*'], // block all event handlers explicitly (DOMPurify also strips them by default)
    ALLOW_DATA_ATTR: false,
  };
}
